import { useNavigationPresentation, tradeLinks, tradeActionVisible } from "@/lib/tradeNavigation";
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import {
  Search, Hammer, Users, Phone, UserCog, LayoutDashboard, Clock, ShieldAlert,
  Receipt, FileText, Truck, MapPin, CalendarDays, Banknote, ClipboardCheck,
  ArrowRight, Loader2, PencilRuler, Package, Zap,
} from "lucide-react";

// Static routes the palette can always jump to (filtered by role at runtime).
const NAV_ROUTES = [
  { label: "Overview", to: "/app", icon: LayoutDashboard, roles: ["super_admin", "admin", "office", "field", "mechanic", "quantity_surveyor"] },
  { label: "Timesheets", to: "/app/timesheets", icon: Clock, roles: ["super_admin", "admin", "office", "field"] },
  { label: "Leave", to: "/app/leave", icon: CalendarDays, roles: ["super_admin", "admin", "office", "field"] },
  { label: "Payroll", to: "/app/payroll", icon: Banknote, roles: ["super_admin", "admin", "office"] },
  { label: "Safety", to: "/app/safety", icon: ShieldAlert, roles: ["super_admin", "admin", "office", "field"] },
  { label: "Toolbox Talks", to: "/app/toolbox", icon: ClipboardCheck, roles: ["super_admin", "admin", "office", "field"] },
  { label: "Site Inspections", to: "/app/inspections", icon: ClipboardCheck, roles: ["super_admin", "admin", "office", "field"] },
  { label: "Jobs", to: "/app/jobs", icon: Hammer, roles: ["super_admin", "admin", "office", "field", "quantity_surveyor"] },
  { label: "Clients", to: "/app/clients", icon: Users, roles: ["super_admin", "admin", "office", "quantity_surveyor"] },
  { label: "Suppliers", to: "/app/suppliers", icon: Phone, roles: ["super_admin", "admin", "office", "field", "quantity_surveyor"] },
  { label: "Team", to: "/app/team", icon: UserCog, roles: ["super_admin", "admin", "office"] },
  { label: "Fleet", to: "/app/fleet", icon: Truck, roles: ["super_admin", "admin", "office", "field", "mechanic", "quantity_surveyor"] },
  { label: "Live Tracking", to: "/app/live-tracking", icon: MapPin, roles: ["super_admin", "admin", "office"] },
  { label: "Takeoffs", to: "/app/takeoffs", icon: PencilRuler, roles: ["super_admin", "admin", "office", "quantity_surveyor"] },
  { label: "Quotes", to: "/app/quotes", icon: FileText, roles: ["super_admin", "admin", "office", "quantity_surveyor"] },
  { label: "Customer Invoices", to: "/app/customer-invoices", icon: Receipt, roles: ["super_admin", "admin", "office", "quantity_surveyor"] },
  { label: "Notice Board", to: "/app/announcements", icon: FileText, roles: ["super_admin", "admin", "office", "field", "mechanic", "quantity_surveyor"] },
  { label: "Reports — Monthly", to: "/app/reports/monthly", icon: FileText, roles: ["super_admin", "admin", "office", "quantity_surveyor"] },
  { label: "Company Settings", to: "/app/branding", icon: UserCog, roles: ["super_admin", "admin", "office"] },
];

// Quick actions — same permission gates as the "+ Create" menu.
const ACTION_ROUTES = [
  { label: "Create Job", to: "/app/jobs?new=1", roles: ["super_admin", "admin", "office"] },
  { label: "Create Takeoff", to: "/app/takeoffs?new=1", roles: ["super_admin", "admin", "office", "quantity_surveyor"] },
  { label: "Create Quote", to: "/app/quotes/new", roles: ["super_admin", "admin", "office", "quantity_surveyor"] },
  { label: "Create Invoice", to: "/app/customer-invoices/new", roles: ["super_admin", "admin", "office", "quantity_surveyor"] },
  { label: "New Timesheet", to: "/app/timesheets?new=1", roles: ["super_admin", "admin", "office", "field"] },
  { label: "Leave Request", to: "/app/leave?new=1", roles: ["super_admin", "admin", "office", "field"] },
  { label: "Log Hazard / Incident", to: "/app/safety?new=1", roles: ["super_admin", "admin", "office", "field"] },
];

const SECTION_ICON = {
  jobs: Hammer, clients: Users, suppliers: Phone, team: UserCog, plant: Truck,
  quotes: FileText, takeoffs: PencilRuler, invoices: Receipt, gear: Package, safety: ShieldAlert,
};
const RESULT_SECTIONS = [
  ["jobs", "Jobs"], ["clients", "Clients"], ["quotes", "Quotes"], ["takeoffs", "Takeoffs"],
  ["invoices", "Invoices"], ["suppliers", "Suppliers"], ["team", "Team"], ["plant", "Plant"],
  ["gear", "Gear"], ["safety", "Safety"],
];
const EMPTY_RESULTS = {
  jobs: [], clients: [], suppliers: [], team: [], plant: [],
  quotes: [], takeoffs: [], invoices: [], gear: [], safety: [],
};

export function CommandPalette({ open, onOpenChange, role }) {
  const { user, trades } = useNavigationPresentation();
  const resultSections = trades ? RESULT_SECTIONS.filter(([key]) => ["jobs", "clients", "quotes", "invoices", "team", "safety"].includes(key)).map(([key, label]) => [key, key === "clients" ? "Customers" : label]) : RESULT_SECTIONS;
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQ("");
      setResults(EMPTY_RESULTS);
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced server search
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) {
      setResults(EMPTY_RESULTS);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/search`, { params: { q } })
        .then((r) => setResults({ ...EMPTY_RESULTS, ...r.data }))
        .catch(() => setResults(EMPTY_RESULTS))
        .finally(() => setLoading(false));
    }, 180);
    return () => { clearTimeout(t); setLoading(false); };
  }, [q, open]);

  // Flatten everything into a single keyboard-nav list
  const navMatches = (trades ? tradeLinks(user) : NAV_ROUTES)
    .filter((r) => r.roles.includes(role))
    .filter((r) => !q.trim() || r.label.toLowerCase().includes(q.toLowerCase()))
    .slice(0, 7);
  const actionMatches = ACTION_ROUTES.filter(r => !trades || tradeActionVisible(r.to))
    .filter((r) => r.roles.includes(role))
    .filter((r) => !q.trim() || r.label.toLowerCase().includes(q.toLowerCase()))
    .slice(0, 5);

  const flatItems = [
    ...navMatches.map((r) => ({ kind: "nav", to: r.to, label: r.label, icon: r.icon, sub: r.to })),
    ...resultSections.flatMap(([sec]) =>
      (results[sec] || []).map((it) => ({
        kind: sec, to: it.link, label: it.title, sub: it.subtitle, icon: SECTION_ICON[sec],
      }))
    ),
    ...actionMatches.map((r) => ({ kind: "action", to: r.to, label: r.label, icon: Zap, sub: "Quick action" })),
  ];

  // Section blocks with running offsets for keyboard navigation
  const sectionBlocks = [];
  {
    let start = navMatches.length;
    for (const [key, title] of resultSections) {
      const n = (results[key] || []).length;
      if (n > 0) sectionBlocks.push({ key, title, start, items: flatItems.slice(start, start + n) });
      start += n;
    }
  }

  const go = useCallback((item) => {
    if (!item) return;
    onOpenChange(false);
    nav(item.to);
  }, [nav, onOpenChange]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flatItems.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(flatItems[activeIdx]); }
  };

  // Clamp active index when items list shrinks
  useEffect(() => {
    if (activeIdx >= flatItems.length) setActiveIdx(Math.max(0, flatItems.length - 1));
  }, [flatItems.length, activeIdx]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 max-w-2xl rounded-sm border-zinc-800 bg-white dark:bg-[#0F0F0F] text-zinc-900 dark:text-white overflow-hidden gap-0"
        data-testid="command-palette"
      >
        <DialogTitle className="sr-only">Global search</DialogTitle>
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-zinc-200 dark:border-zinc-800">
          {loading
            ? <Loader2 className="w-5 h-5 text-[#FF5F15] shrink-0 animate-spin" />
            : <Search className="w-5 h-5 text-[#FF5F15] shrink-0" />}
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setActiveIdx(0); }}
            onKeyDown={onKeyDown}
            data-testid="command-palette-input"
            placeholder={trades ? "Search jobs, customers, quotes, invoices and people…" : "Search anything — jobs, quotes, takeoffs, invoices, plant, people..."}
            className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
          />
          <kbd className="hidden md:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-500 border border-zinc-300 dark:border-zinc-700 rounded-sm">
            ESC
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto py-2" data-testid="command-palette-results">
          {flatItems.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-500" data-testid="command-palette-empty">
              {q ? `No results for "${q}"` : "Start typing to search"}
            </div>
          ) : (
            <>
              {navMatches.length > 0 && (
                <Section title="Pages" startIdx={0} active={activeIdx} setActive={setActiveIdx} items={flatItems.slice(0, navMatches.length)} onPick={go} />
              )}
              {sectionBlocks.map((b) => (
                <Section key={b.key} title={b.title} startIdx={b.start} active={activeIdx} setActive={setActiveIdx} items={b.items} onPick={go} />
              ))}
              {actionMatches.length > 0 && (
                <Section title="Actions" startIdx={flatItems.length - actionMatches.length} active={activeIdx} setActive={setActiveIdx} items={flatItems.slice(flatItems.length - actionMatches.length)} onPick={go} />
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-4 py-2 border-t border-zinc-200 dark:border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          <div className="flex items-center gap-3">
            <span><kbd className="font-bold text-zinc-700 dark:text-zinc-300">↑↓</kbd> navigate</span>
            <span><kbd className="font-bold text-zinc-700 dark:text-zinc-300">↵</kbd> open</span>
          </div>
          <div className="text-[#FF5F15] font-bold">FIELDLINK · CMD-K</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, items, startIdx, active, setActive, onPick }) {
  return (
    <div className="pb-1">
      <div className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500 font-mono">
        // {title}
      </div>
      {items.map((it, i) => {
        const idx = startIdx + i;
        const Icon = it.icon || ArrowRight;
        const isActive = idx === active;
        return (
          <button
            key={`${it.kind}-${it.to}-${i}`}
            type="button"
            data-testid={`command-item-${it.kind}-${i}`}
            onClick={() => onPick(it)}
            onMouseEnter={() => setActive(idx)}
            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
              isActive
                ? "bg-[#FF5F15]/10 text-[#FF5F15]"
                : "hover:bg-zinc-100 dark:hover:bg-[#151C27] text-zinc-800 dark:text-zinc-200"
            }`}
          >
            <Icon className="w-4 h-4 shrink-0" strokeWidth={2} />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold truncate">{it.label}</div>
              {it.sub && (
                <div className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate font-mono uppercase tracking-wider">
                  {it.sub}
                </div>
              )}
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
          </button>
        );
      })}
    </div>
  );
}
